import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable } from 'knockout';
import { EffectsContainer } from 'mixins/withEffects';
import { JSONVideoPresentationEvent } from 'models/videoPresentationEvent';
import VideoPresentationProgress, { JSONVideoPresentationProgress } from 'models/videoPresentationProgress';
import { ServicesContext } from 'services/context';
import { EntityDataSource } from './datasources/entity';
import events from './events';
import { IWCCStorageManager } from './iStorage';
import { TopicManager } from './topic';

export interface VideoPresentationProgressManagerConfig {
    topicId: string
}

@injectable()
export default class VideoPresentationProgressManager {
    private source: EntityDataSource<JSONVideoPresentationProgress>
    private topicId: string    

    progress: PureComputed<VideoPresentationProgress | undefined>

    isWatched: Subscribable<boolean>
    isFinished: Subscribable<boolean>

    busy = ko.pureComputed(() => this.source.busy())
    loading = ko.pureComputed(() => this.busy() && this.source.requestsProcessed() === 0);
    updating = ko.pureComputed(() => this.busy() && this.source.requestsProcessed() > 0);

    constructor(
        @inject(wccModules.managerConfig) config: VideoPresentationProgressManagerConfig,
        @inject(wccModules.servicesContext) private ctx: ServicesContext,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.topicId = config.topicId;

        const topicManager = storage.get(TopicManager, { topicId: this.topicId });
        const topic = topicManager.pluck(m => m.topic);
        const vpOptions = topic.pluck(t => t.videoPresentationOptions);

        this.source = effects.register(new EntityDataSource({
            update: this.update.bind(this)
        }));

        this.progress = this.source.data.toEntity(
            jsonProgress => new VideoPresentationProgress(jsonProgress), 
            (progress, jsonProgress) => progress.update(jsonProgress));

        this.isWatched = this.progress.pluck(p => p.attemptNumber() > 1 || p.preciseProgress() === 100, false);

        this.isFinished = ko.pureComputed(() => {
            const currentTopic = topic();
            const options = vpOptions();
            const progress = this.progress();

            if (currentTopic != undefined && options != undefined) {
                if (options.presentationType() == enums.VideoPresentationTypes.PresentationAndSurvey.value) {
                    return currentTopic.isAnswered();
                } else if (progress != undefined) {
                    const maxWatchAmount = options.maxWatchAmount() ?? 0;

                    return maxWatchAmount > 0 &&
                        (progress.attemptNumber() > maxWatchAmount || progress.attemptNumber() === maxWatchAmount && progress.preciseProgress() === 100);
                }
            }

            return false;
        });

        effects.register([
            events.videoPresentationEvent.on(this.onLocalVideoPresentationEvent.bind(this))
        ]);
    }

    private update() {
        return this.ctx.topicsService.getVideoPresentationProgress(this.topicId);
    }

    private onLocalVideoPresentationEvent(topicId: string, eventData: JSONVideoPresentationEvent) {
        if (this.topicId === topicId) {
            const progress = this.progress();

            if (progress != undefined) {
                if (progress.preciseProgress() === 100 && eventData.Percent < 1)
                    progress.attemptNumber.inc();

                progress.preciseProgress(eventData.Percent * 100);
                progress.currentPosition(eventData.Seconds);
            } else {
                this.source.update();
            }
        }
    }
}